<script setup>
import { useApiRequest } from '@/composables/useApiRequest'
import { formatDate, formatPercent, formatRupiah } from '@/composables/useFormat'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Settings',
  },
})

const router = useRouter()
const route = useRoute()
const ability = useAbility()
const { loading, errors, request } = useApiRequest()

// Addendum 05, Modul L: perpanjangan bersifat billing, owner-only (lihat
// RolePermissionDefaults — permission Subscription.renew sengaja tidak
// diberikan ke manager/cashier/kitchen_staff), jadi tab ini disembunyikan
// sama sekali untuk role selain owner alih-alih ditampilkan tapi setiap
// aksinya gagal di backend.
const canRenewSubscription = computed(() => ability.can('renew', 'Subscription'))

// Addendum 12, Modul AZ: pembelian addon bersifat billing, owner-only
// (lihat RolePermissionDefaults — permission Addon.purchase sengaja tidak
// diberikan ke manager/cashier/kitchen_staff) — sama pola persis dengan
// canRenewSubscription di atas.
const canManageAddons = computed(() => ability.can('purchase', 'Addon'))

// Addendum 08 patch — tab Absensi (pengaturan lokasi/geofence) di-gate ke
// Attendance.manage, SAMA gerbang dengan endpoint PUT-nya sendiri (owner
// saja secara default, lihat RolePermissionDefaults) — sama prinsip dengan
// canRenewSubscription di atas: tab disembunyikan sama sekali untuk role
// yang aksinya pasti gagal di backend, bukan ditampilkan lalu gagal saat submit.
const canManageAttendance = computed(() => ability.can('manage', 'Attendance'))

// Addendum 10, Modul AL — tab "Tipe Bisnis" (toggle F&B/Retail) di-gate ke
// Tenant.manage_features, SAMA gerbang dengan endpoint PUT-nya sendiri
// (owner saja secara default, lihat RolePermissionDefaults) — sama prinsip
// dengan canManageAttendance di atas.
const canManageFeatures = computed(() => ability.can('manage_features', 'Tenant'))

// -----------------------------------------------------------------------
// Tabs — ditambah tab AI Status & Langganan
// -----------------------------------------------------------------------
const tabs = computed(() => [
  { value: 'general',   label: 'Umum',             icon: 'ri-store-2-line' },
  { value: 'tax',       label: 'Pajak & Keuangan', icon: 'ri-percent-line' },
  { value: 'receipt',   label: 'Struk',             icon: 'ri-receipt-line' },
  { value: 'email',     label: 'Email / SMTP',      icon: 'ri-mail-settings-line' },
  { value: 'payment',   label: 'Payment Gateway',  icon: 'ri-bank-card-line' },
  { value: 'inventory', label: 'Inventory',         icon: 'ri-archive-line' },

  // Addendum 08 patch — TAMBAHAN BARU
  ...(canManageAttendance.value
    ? [{ value: 'attendance', label: 'Absensi', icon: 'ri-fingerprint-line' }]
    : []),

  // Addendum 10, Modul AL — TAMBAHAN BARU
  ...(canManageFeatures.value
    ? [{ value: 'business-type', label: 'Tipe Bisnis', icon: 'ri-store-3-line' }]
    : []),

  { value: 'ai-status', label: 'Status Layanan AI', icon: 'ri-robot-line' },

  // Addendum 05, Modul L — TAMBAHAN BARU
  ...(canRenewSubscription.value
    ? [{ value: 'langganan', label: 'Langganan', icon: 'ri-vip-crown-line' }]
    : []),

  // Addendum 12, Modul AZ — TAMBAHAN BARU
  ...(canManageAddons.value
    ? [{ value: 'addon', label: 'Addon', icon: 'ri-puzzle-line' }]
    : []),
])

// Active tab dari URL param — default ke 'general'
const activeTab = computed({
  get: () => route.params.tab ?? 'general',
  set: (val) => router.push({ name: 'settings-tab', params: { tab: val } }),
})

// Redirect /settings → /settings/general
onMounted(() => {
  if (!route.params.tab) {
    router.replace({ name: 'settings-tab', params: { tab: 'general' } })
  }
})

// -----------------------------------------------------------------------
// State
// -----------------------------------------------------------------------
const formRef = ref()
const successMessage = ref('')
const isFetching = ref(false)

const tenant = ref({
  name: '',
  email: '',
  phone: '',
  address: '',
  logo: null,
  receipt_logo: null,
})

const settings = ref({
  tax_rate: 0.11,
  currency: 'IDR',
  timezone: 'Asia/Jakarta',
  allow_negative_stock: false,
  receipt_footer: 'Terima kasih sudah berkunjung!',
  receipt_show_logo: true,
  low_stock_threshold: 10,

  // ✅ TAMBAHAN: SMTP per-tenant (dipakai OrderController::sendEmail)
  smtp_host: '',
  smtp_port: 587,
  smtp_encryption: 'tls',
  smtp_username: '',
  smtp_password: '', // selalu kosong dari server (redacted) — isi = mau ganti
  smtp_from_name: '',
  smtp_from_email: '',

  // ✅ TAMBAHAN: Payment Gateway per-tenant
  payment_gateway_provider: 'midtrans',
  payment_gateway_is_production: false,
  payment_gateway_midtrans_server_key: '',    // selalu kosong dari server (redacted) — isi = mau ganti
  payment_gateway_xendit_secret_key: '',     // idem
  payment_gateway_xendit_callback_token: '', // idem

  // ✅ TAMBAHAN (Addendum 06, Modul N): kredensial iPaymu — sama-sama redacted
  payment_gateway_ipaymu_va: '',
  payment_gateway_ipaymu_api_key: '',
})

// Dari backend: apakah smtp_password sudah pernah diisi sebelumnya.
// Password aslinya tidak pernah dikirim ke frontend (lihat SettingController::show).
const smtpPasswordConfigured = ref(false)

// Sama seperti smtpPasswordConfigured — status "sudah diisi atau belum" untuk
// tiap key rahasia payment gateway, nilainya sendiri tidak pernah dikirim server.
const midtransServerKeyConfigured = ref(false)
const xenditSecretKeyConfigured = ref(false)
const xenditCallbackTokenConfigured = ref(false)
const ipaymuVaConfigured = ref(false)
const ipaymuApiKeyConfigured = ref(false)

const paymentProviderOptions = [
  { title: 'Midtrans', value: 'midtrans' },
  { title: 'Xendit', value: 'xendit' },
  { title: 'iPaymu', value: 'ipaymu' },
]

// URL webhook yang harus didaftarkan tenant ke dashboard merchant Midtrans/Xendit/iPaymu
// masing-masing — satu URL untuk SEMUA tenant, karena tenant di-resolve dari
// order_number/external_id/reference_id di payload webhook (lihat PaymentGatewayService).
// Pakai VITE_API_BASE_URL yang sama seperti utils/api.js, supaya konsisten kalau
// API di-host di domain/port berbeda dari frontend (bukan cuma window.location.origin).
const apiBaseUrl = (() => {
  const configured = import.meta.env.VITE_API_BASE_URL
  if (configured && /^https?:\/\//.test(configured)) return configured.replace(/\/$/, '')
  return `${window.location.origin}${configured || '/api'}`.replace(/\/$/, '')
})()
const midtransWebhookUrl = `${apiBaseUrl}/v1/webhooks/midtrans`
const xenditWebhookUrl = `${apiBaseUrl}/v1/webhooks/xendit`
const ipaymuWebhookUrl = `${apiBaseUrl}/v1/webhooks/ipaymu`

// -----------------------------------------------------------------------
// Upload logo — endpoint SUDAH ADA di backend (SettingController::uploadLogo
// & uploadReceiptLogo, route POST /api/v1/settings/logo & /settings/receipt-logo)
// tapi sebelumnya TIDAK ADA UI untuk memanggilnya sama sekali. Ini beda dari
// field name/email/phone/address di atas (dikunci superadmin) — logo memang 
// didesain bisa diubah tenant sendiri kapan saja, jadi tidak perlu tunggu
// platform admin.
// -----------------------------------------------------------------------
const logoFile = ref(null)
const logoUploading = ref(false)
const logoError = ref('')

const uploadLogo = async () => {
  if (!logoFile.value) return

  logoUploading.value = true
  logoError.value = ''
  try {
    // PENTING: VFileInput di Vuetify 3 v-model-nya SELALU array (File[]),
    // walau mode single (bukan multiple) — beda dari Vuetify 2 yang single
    // File. Tanpa unwrap ini, FormData.append() dikirimi array (jadi string
    // "[object File]"), bukan file aslinya.
    const file = Array.isArray(logoFile.value) ? logoFile.value[0] : logoFile.value

    if (!file) return

    const formData = new FormData()
    formData.append('logo', file)

    // useApi().post() sudah otomatis melepas header Content-Type kalau body
    // FormData (lihat utils/api.js), jadi browser yang set multipart boundary.
    await useApi('/api/v1/settings/logo').post({ body: formData })

    // Re-fetch dari GET /settings (bukan cuma pakai field dari response
    // upload) — supaya tampilan selalu sinkron dengan apa yang benar-benar 
    // tersimpan di server, apapun bentuk response upload-nya.
    await fetchSettings()

    logoFile.value = null
    successMessage.value = 'Logo toko berhasil diperbarui.'
    setTimeout(() => { successMessage.value = '' }, 3000)
  } catch (err) {
    const data = err?.data ?? err?.response?._data
    logoError.value = data?.message ?? 'Gagal upload logo. Pastikan file berupa gambar, maksimal 2MB.'
  } finally {
    logoUploading.value = false
  }
}

const receiptLogoFile = ref(null)
const receiptLogoUploading = ref(false)
const receiptLogoError = ref('')

const uploadReceiptLogo = async () => {
  if (!receiptLogoFile.value) return

  receiptLogoUploading.value = true
  receiptLogoError.value = ''
  try {
    const file = Array.isArray(receiptLogoFile.value) ? receiptLogoFile.value[0] : receiptLogoFile.value

    if (!file) return

    const formData = new FormData()
    formData.append('logo', file) // nama field 'logo' sesuai SettingController::uploadReceiptLogo

    await useApi('/api/v1/settings/receipt-logo').post({ body: formData })

    await fetchSettings()

    receiptLogoFile.value = null
    successMessage.value = 'Logo struk berhasil diperbarui.'
    setTimeout(() => { successMessage.value = '' }, 3000)
  } catch (err) {
    const data = err?.data ?? err?.response?._data
    receiptLogoError.value = data?.message ?? 'Gagal upload logo struk. Pastikan file berupa gambar, maksimal 2MB.'
  } finally {
    receiptLogoUploading.value = false
  }
}

const encryptionOptions = [
  { title: 'TLS (disarankan, port 587)', value: 'tls' },
  { title: 'SSL (port 465)', value: 'ssl' },
  { title: 'Tanpa enkripsi', value: 'none' },
]

const timezoneOptions = [
  { title: 'WIB (Asia/Jakarta)', value: 'Asia/Jakarta' },
  { title: 'WITA (Asia/Makassar)', value: 'Asia/Makassar' },
  { title: 'WIT (Asia/Jayapura)', value: 'Asia/Jayapura' },
]

// -----------------------------------------------------------------------
// AI Status State — untuk tab AI Status
// -----------------------------------------------------------------------
const aiLoading = ref(false)
const voiceStatus = ref(null)
const receiptStatus = ref(null)
const lastFetchedAt = ref(null)
let pollTimer = null

const loadAiStatus = async () => {
  aiLoading.value = true
  try {
    const res = await request(() => useApi('/api/v1/settings/ai-status').get())
    const data = res?.data ?? res

    if (data) {
      voiceStatus.value = data.voice_to_order
      receiptStatus.value = data.receipt_scanner
      lastFetchedAt.value = new Date()
    }
  } finally {
    aiLoading.value = false
  }
}

const statusMeta = (status) => {
  switch (status) {
    case 'healthy':
      return { color: 'success', icon: 'ri-checkbox-circle-fill', label: 'Normal' }
    case 'down':
      return { color: 'error', icon: 'ri-close-circle-fill', label: 'Tidak Merespons' }
    default:
      return { color: 'grey', icon: 'ri-question-line', label: 'Belum Diketahui' }
  }
}

const formatCheckedAt = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('id-ID', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

// -----------------------------------------------------------------------
// Addendum 05, Modul L — State & aksi untuk tab Langganan (perpanjangan
// self-service tenant existing). Terpisah dari state `settings` di atas —
// tab ini tidak lewat PUT /api/v1/settings sama sekali, form-nya sendiri
// (mirip tab AI Status yang juga punya tombol aksi sendiri, bukan "Simpan
// Pengaturan" generik).
// -----------------------------------------------------------------------
// 2026-08-07: bulanan dihapus (keputusan produk: mengurangi frekuensi
// penagihan manual) -- sekarang 3 bulan / 6 bulan / tahunan.
const BILLING_PERIODS = ['quarterly', 'semi_annual', 'yearly']
const BILLING_PERIOD_LABELS = { quarterly: '3 Bulan', semi_annual: '6 Bulan', yearly: 'Tahunan' }

// Harga PER BULAN untuk periode tertentu. price_semi_annual_per_month
// datang langsung dari API (PlanResource) supaya diskonnya tidak perlu
// di-hardcode di frontend -- quarterly = price_monthly (0% diskon).
const perMonthPrice = (plan, period) => {
  if (!plan) return null

  return {
    quarterly: plan.price_monthly,
    semi_annual: plan.price_semi_annual_per_month,
    yearly: plan.price_yearly_per_month,
  }[period]
}

const subscriptionLoading = ref(false)
const subscriptionInfo = ref(null)
const availablePlans = ref([])
const selectedPlanId = ref(null)
const billingPeriod = ref('quarterly')
const renewLoading = ref(false)
const renewError = ref('')

const fetchSubscription = async () => {
  subscriptionLoading.value = true
  try {
    const response = await useApi('/api/v1/subscription').get()
    const data = response?.data ?? response

    subscriptionInfo.value = data?.subscription ?? null
  } catch (err) {
    console.error('❌ Fetch subscription error:', err)
  } finally {
    subscriptionLoading.value = false
  }
}

const fetchAvailablePlans = async () => {
  try {
    const response = await useApi('/api/public/plans').get()
    const body = response?.data ?? response

    availablePlans.value = Array.isArray(body) ? body : (body?.data ?? [])

    // Default pilihan: paket yang sedang dipakai kalau ada, kalau tidak
    // paket pertama — supaya tenant tidak wajib klik dulu sebelum bisa
    // langsung "Perpanjang" paket yang sama.
    if (!selectedPlanId.value) {
      const current = availablePlans.value.find(p => p.id === subscriptionInfo.value?.plan?.id)
      selectedPlanId.value = (current ?? availablePlans.value[0])?.id ?? null
    }
  } catch (err) {
    console.error('❌ Fetch plans error:', err)
  }
}

const renewSubscription = async () => {
  if (!selectedPlanId.value) return

  renewError.value = ''
  renewLoading.value = true
  try {
    const response = await useApi('/api/v1/subscription/renew').post({
      body: {
        plan_id: selectedPlanId.value,
        billing_period: billingPeriod.value,
      },
    })
    const data = response?.data ?? response

    if (data?.invoice_url) {
      // Redirect penuh (bukan SPA nav) — halaman invoice Xendit di domain lain.
      window.location.href = data.invoice_url
    }
  } catch (err) {
    const errData = err?.data ?? err?.response?._data
    renewError.value = errData?.message ?? 'Gagal membuat order perpanjangan. Silakan coba lagi.'
  } finally {
    renewLoading.value = false
  }
}

// Addendum 13, Modul BG — unduh ulang invoice PDF pendaftaran awal.
// invoiceAvailable default true supaya tombol langsung tampil (tenant baru
// dari alur /daftar selalu punya invoice); begitu 404 (tenant dibuat manual
// lewat /platform, tidak pernah lewat /daftar -- tidak ada baris
// customer_registrations sama sekali) tombol disembunyikan permanen untuk
// sesi ini, tidak perlu endpoint terpisah cuma untuk cek ada/tidaknya.
const invoiceDownloading = ref(false)
const invoiceAvailable = ref(true)

const downloadInvoice = async () => {
  if (invoiceDownloading.value) return

  invoiceDownloading.value = true
  try {
    const blob = await useApi('/api/v1/subscription/invoice').get({ responseType: 'blob' })

    const objectUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = objectUrl
    link.download = 'invoice-pintukasir.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(objectUrl)
  } catch (err) {
    const status = err?.status ?? err?.response?.status

    if (status === 404) {
      invoiceAvailable.value = false
    } else {
      console.error('❌ Download invoice error:', err)
    }
  } finally {
    invoiceDownloading.value = false
  }
}

// -----------------------------------------------------------------------
// Addendum 12, Modul AZ — State & aksi tab "Kelola Addon". Endpoint
// TERPISAH dari PUT /api/v1/settings, sama pola dengan tab Langganan di
// atas (billing, bukan bagian dari tenant.settings JSON). Satu tombol
// "Beli & Aktifkan"/"Perpanjang & Bayar" per addon — dibedakan cuma dari
// label tombol (is_active_for_tenant), endpoint & payload-nya identik.
// -----------------------------------------------------------------------
const addonsLoading = ref(false)
const addons = ref([])
const addonBillingPeriod = ref({})
const addonPurchaseLoading = ref({})
const addonPurchaseError = ref('')

const fetchAddons = async () => {
  addonsLoading.value = true
  try {
    const response = await useApi('/api/v1/addons').get()
    const data = response?.data ?? response
    addons.value = data?.addons ?? []
    addons.value.forEach(addon => {
      if (!addonBillingPeriod.value[addon.id]) addonBillingPeriod.value[addon.id] = 'monthly'
    })
  } catch (err) {
    console.error('❌ Fetch addons error:', err)
  } finally {
    addonsLoading.value = false
  }
}

const purchaseAddon = async (addon) => {
  addonPurchaseError.value = ''
  addonPurchaseLoading.value[addon.id] = true
  try {
    const response = await useApi(`/api/v1/addons/${addon.id}/purchase`).post({
      body: { billing_period: addonBillingPeriod.value[addon.id] },
    })
    const data = response?.data ?? response

    if (data?.invoice_url) {
      // Redirect penuh (bukan SPA nav) — halaman invoice Xendit di domain lain.
      window.location.href = data.invoice_url
    }
  } catch (err) {
    const errData = err?.data ?? err?.response?._data
    addonPurchaseError.value = errData?.message ?? 'Gagal membuat order pembelian addon. Silakan coba lagi.'
  } finally {
    addonPurchaseLoading.value[addon.id] = false
  }
}

// -----------------------------------------------------------------------
// Addendum 08 patch — State & aksi tab Absensi (pengaturan lokasi toko /
// geofence, mengisi tabel attendance_settings — Modul Y dulu cuma bikin
// kolomnya tanpa halaman UI). Endpoint TERPISAH dari PUT /api/v1/settings
// (settings di atas), karena attendance_settings adalah tabel sendiri,
// bukan bagian dari tenant.settings JSON — sama alasan tab Langganan di
// atas juga punya state & aksi sendiri (bukan lewat save() generik).
// -----------------------------------------------------------------------
const attendanceSettings = ref({
  is_active: false,
  require_attendance_for_shift: false,
  photo_capture_enabled: false,
  store_latitude: null,
  store_longitude: null,
  geofence_radius_meters: 100,
  late_tolerance_minutes: 0,
})
const attendanceLoaded = ref(false)
const attendanceSuccessMessage = ref('')
const {
  loading: attendanceLoading,
  errors: attendanceErrors,
  request: attendanceRequest,
} = useApiRequest()

const fetchAttendanceSettings = async () => {
  const res = await attendanceRequest(() => useApi('/api/v1/attendance/settings').get())

  if (res) {
    const data = res?.data ?? res

    attendanceSettings.value = { ...attendanceSettings.value, ...data }
  }
  attendanceLoaded.value = true
}

// Body dikirim eksplisit dikonversi ke number/null — TIDAK andalkan
// v-model.number di textfield (kalau field dikosongkan, modifier itu bisa
// menyisakan string kosong '' alih-alih null, sedangkan backend
// (AttendanceSettingRequest) butuh null yang sesungguhnya untuk
// "lokasi belum diatur, lewati validasi geofence" — lihat komentar
// AttendanceService::assertWithinGeofence()).
const attendanceSavePayload = () => {
  const s = attendanceSettings.value
  const toNumberOrNull = v => (v === '' || v === null || v === undefined ? null : Number(v))

  return {
    is_active: !!s.is_active,
    require_attendance_for_shift: !!s.require_attendance_for_shift,
    photo_capture_enabled: !!s.photo_capture_enabled,
    store_latitude: toNumberOrNull(s.store_latitude),
    store_longitude: toNumberOrNull(s.store_longitude),
    geofence_radius_meters: Number(s.geofence_radius_meters),
    late_tolerance_minutes: Number(s.late_tolerance_minutes),
  }
}

const saveAttendanceSettings = async () => {
  attendanceSuccessMessage.value = ''

  const res = await attendanceRequest(() =>
    useApi('/api/v1/attendance/settings').put({ body: attendanceSavePayload() })
  )

  if (res) {
    const data = res?.data ?? res

    attendanceSettings.value = { ...attendanceSettings.value, ...data }
    attendanceSuccessMessage.value = 'Pengaturan absensi berhasil disimpan.'
    setTimeout(() => { attendanceSuccessMessage.value = '' }, 3000)
  }
}

// -----------------------------------------------------------------------
// Addendum 10, Modul AL — State & aksi tab "Tipe Bisnis" (toggle F&B/Retail).
// Endpoint TERPISAH dari PUT /api/v1/settings (settings di atas), sama pola
// dengan tab Absensi di atas — ini kolom tenant sendiri (is_fnb_enabled/
// is_retail_enabled), bukan bagian dari tenant.settings JSON, dan digerbang
// permission tersendiri (Tenant.manage_features) yang lebih ketat dari
// Settings.update biasa.
// -----------------------------------------------------------------------
const businessType = ref({
  is_fnb_enabled: true,
  is_retail_enabled: false,
})
const businessTypeSuccessMessage = ref('')
const { loading: businessTypeLoading, request: businessTypeRequest } = useApiRequest()

const saveBusinessType = async () => {
  businessTypeSuccessMessage.value = ''

  const res = await businessTypeRequest(() =>
    useApi('/api/v1/settings/features').put({ body: businessType.value })
  )

  if (res) {
    const data = res?.data ?? res

    businessType.value = { ...businessType.value, ...data }
    businessTypeSuccessMessage.value = 'Pengaturan tipe bisnis berhasil disimpan.'
    setTimeout(() => { businessTypeSuccessMessage.value = '' }, 3000)
  }
}

// "Ambil Lokasi Saat Ini" — GPS browser (Geolocation API), supaya Owner
// tidak perlu cari koordinat toko secara manual (mis. dari Google Maps).
const gpsLoading = ref(false)
const gpsError = ref('')

const useCurrentLocation = () => {
  if (!navigator.geolocation) {
    gpsError.value = 'Perangkat/browser ini tidak mendukung GPS.'
    return
  }

  gpsLoading.value = true
  gpsError.value = ''
  navigator.geolocation.getCurrentPosition(
    position => {
      attendanceSettings.value.store_latitude = position.coords.latitude
      attendanceSettings.value.store_longitude = position.coords.longitude
      gpsLoading.value = false
    },
    err => {
      gpsError.value = err?.code === err?.PERMISSION_DENIED
        ? 'Izin lokasi ditolak. Aktifkan izin lokasi untuk browser ini lalu coba lagi.'
        : 'Gagal mengambil lokasi GPS. Pastikan GPS aktif dan coba lagi.'
      gpsLoading.value = false
    },
    { enableHighAccuracy: true, timeout: 10000 }
  )
}

// -----------------------------------------------------------------------
// Fetch — ✅ FIX: response?.data ?? response (addendum 5A.4)
// -----------------------------------------------------------------------
const fetchSettings = async () => {
  isFetching.value = true
  try {
    const response = await useApi('/api/v1/settings').get()
    const data = response?.data ?? response

    tenant.value = {
      name:         data.tenant.name          ?? '',
      email:        data.tenant.email         ?? '',
      phone:        data.tenant.phone         ?? '',
      address:      data.tenant.address       ?? '',
      logo:         data.tenant.logo          ?? null,
      receipt_logo: data.tenant.receipt_logo  ?? null,
    }
    // Addendum 10, Modul AL — sekalian ambil state toggle F&B/Retail dari
    // GET /settings (SettingController::show() sudah expose ini di block
    // tenant), tidak perlu endpoint fetch terpisah.
    if (canManageFeatures.value) {
      businessType.value = {
        is_fnb_enabled: data.tenant.is_fnb_enabled ?? true,
        is_retail_enabled: data.tenant.is_retail_enabled ?? false,
      }
    }
    settings.value = {
      ...settings.value,
      ...data.settings,
      smtp_password: '',
      payment_gateway_midtrans_server_key: '',
      payment_gateway_xendit_secret_key: '',
      payment_gateway_xendit_callback_token: '',
      payment_gateway_ipaymu_va: '',
      payment_gateway_ipaymu_api_key: '',
    }
    smtpPasswordConfigured.value = !!data.settings?.smtp_password_configured
    midtransServerKeyConfigured.value = !!data.settings?.payment_gateway_midtrans_server_key_configured
    xenditSecretKeyConfigured.value = !!data.settings?.payment_gateway_xendit_secret_key_configured
    xenditCallbackTokenConfigured.value = !!data.settings?.payment_gateway_xendit_callback_token_configured
    ipaymuVaConfigured.value = !!data.settings?.payment_gateway_ipaymu_va_configured
    ipaymuApiKeyConfigured.value = !!data.settings?.payment_gateway_ipaymu_api_key_configured
  } finally {
    isFetching.value = false
  }
}

// -----------------------------------------------------------------------
// Save — ✅ FIX: sama
// -----------------------------------------------------------------------
const save = async () => {
  successMessage.value = ''

  const res = await request(() =>
    useApi('/api/v1/settings').put({ body: settings.value })
  )

  if (res) {
    const data = res?.data ?? res
    settings.value = {
      ...settings.value,
      ...(data.settings ?? {}),
      smtp_password: '',
      payment_gateway_midtrans_server_key: '',
      payment_gateway_xendit_secret_key: '',
      payment_gateway_xendit_callback_token: '',
      payment_gateway_ipaymu_va: '',
      payment_gateway_ipaymu_api_key: '',
    }
    smtpPasswordConfigured.value = !!data.settings?.smtp_password_configured
    midtransServerKeyConfigured.value = !!data.settings?.payment_gateway_midtrans_server_key_configured
    xenditSecretKeyConfigured.value = !!data.settings?.payment_gateway_xendit_secret_key_configured
    xenditCallbackTokenConfigured.value = !!data.settings?.payment_gateway_xendit_callback_token_configured
    ipaymuVaConfigured.value = !!data.settings?.payment_gateway_ipaymu_va_configured
    ipaymuApiKeyConfigured.value = !!data.settings?.payment_gateway_ipaymu_api_key_configured
    successMessage.value = 'Pengaturan berhasil disimpan.'
    setTimeout(() => { successMessage.value = '' }, 3000)
  }
}

// Tax rate display: 0.11 ↔ 11
const taxRatePercent = computed({
  get: () => Math.round(settings.value.tax_rate * 100 * 100) / 100,
  set: (v) => { settings.value.tax_rate = parseFloat(v) / 100 },
})

onMounted(() => {
  fetchSettings()

  // Load AI status jika tab aktif adalah ai-status
  if (activeTab.value === 'ai-status') {
    loadAiStatus()
    // Auto-refresh tiap 30 detik hanya jika di tab AI
    pollTimer = setInterval(loadAiStatus, 30000)
  }

  // Addendum 05, Modul L — load status langganan + daftar paket kalau tab
  // aktif langsung 'langganan' (mis. datang dari link banner dashboard).
  if (activeTab.value === 'langganan' && canRenewSubscription.value) {
    fetchSubscription().then(fetchAvailablePlans)
  }

  // Addendum 12, Modul AZ — sama pola, kalau tab aktif langsung 'addon'.
  if (activeTab.value === 'addon' && canManageAddons.value) {
    fetchAddons()
  }

  // Addendum 08 patch — sama pola, kalau tab aktif langsung 'attendance'.
  if (activeTab.value === 'attendance' && canManageAttendance.value) {
    fetchAttendanceSettings()
  }
})

// Watch tab changes untuk AI status polling & load data tab Langganan
watch(activeTab, (newTab) => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }

  if (newTab === 'langganan' && canRenewSubscription.value && !subscriptionInfo.value) {
    fetchSubscription().then(fetchAvailablePlans)
  }

  if (newTab === 'addon' && canManageAddons.value && !addons.value.length) {
    fetchAddons()
  }

  if (newTab === 'attendance' && canManageAttendance.value && !attendanceLoaded.value) {
    fetchAttendanceSettings()
  }

  if (newTab === 'ai-status') {
    loadAiStatus()
    pollTimer = setInterval(loadAiStatus, 30000)
  }
})

onUnmounted(() => {
  if (pollTimer) {
    clearInterval(pollTimer)
  }
})
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Pengaturan</h4>
        <p class="text-body-2 text-medium-emphasis">
          Konfigurasi toko dan preferensi sistem
        </p>
      </VCol>
    </VRow>

    <VRow>
      <!-- Sidebar tab navigation -->
      <VCol cols="12" md="3">
        <VCard>
          <VList nav>
            <VListItem
              v-for="tab in tabs"
              :key="tab.value"
              :active="activeTab === tab.value"
              active-color="primary"
              rounded="lg"
              @click="activeTab = tab.value"
            >
              <template #prepend>
                <VIcon :icon="tab.icon" size="20" />
              </template>
              <VListItemTitle>{{ tab.label }}</VListItemTitle>
            </VListItem>
          </VList>
        </VCard>
      </VCol>

      <!-- Content -->
      <VCol cols="12" md="9">
        <VProgressLinear v-if="isFetching || (activeTab === 'ai-status' && aiLoading)" indeterminate color="primary" class="mb-4" />

        <VAlert
          v-if="successMessage"
          type="success"
          variant="tonal"
          density="compact"
          class="mb-4"
          closable
          @click:close="successMessage = ''"
        >
          {{ successMessage }}
        </VAlert>

        <VForm ref="formRef" @submit.prevent="save">
          <!-- ============================================================
               Tab: Umum
          ============================================================ -->
          <VCard v-show="activeTab === 'general'">
            <VCardTitle class="pa-4 pb-2">Informasi Toko</VCardTitle>
            <VCardText>
              <div class="mb-4">
                <p class="text-body-2 font-weight-medium mb-2">
                  Logo Toko
                </p>
                <div class="d-flex align-center gap-4 flex-wrap">
                  <VAvatar
                    v-if="tenant.logo"
                    size="80"
                    rounded
                  >
                    <VImg :src="tenant.logo" />
                  </VAvatar>
                  <VAvatar
                    v-else
                    size="80"
                    rounded
                    color="secondary"
                    variant="tonal"
                  >
                    <VIcon icon="ri-store-2-line" size="32" />
                  </VAvatar>
                  <VFileInput
                    v-model="logoFile"
                    label="Pilih logo baru"
                    accept="image/*"
                    density="compact"
                    prepend-icon=""
                    prepend-inner-icon="ri-image-add-line"
                    hide-details
                    show-size
                    class="flex-grow-1"
                    style="min-width: 220px;"
                  />
                  <VBtn
                    color="primary"
                    :disabled="!logoFile"
                    :loading="logoUploading"
                    @click="uploadLogo"
                  >
                    Upload Logo
                  </VBtn>
                </div>
                <p class="text-caption text-medium-emphasis mt-2 mb-0">
                  Format gambar (PNG/JPG), maksimal 2MB. Beda dengan nama/email/telepon di bawah,
                  logo boleh diubah kapan saja tanpa lewat support.
                </p>
                <VAlert
                  v-if="logoError"
                  type="error"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  {{ logoError }}
                </VAlert>
              </div>

              <VRow>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="tenant.name"
                    label="Nama Toko"
                    prepend-inner-icon="ri-store-2-line"
                    disabled
                    hint="Hubungi support untuk mengubah nama toko"
                    persistent-hint
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="tenant.email"
                    label="Email Toko"
                    prepend-inner-icon="ri-mail-line"
                    disabled
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="tenant.phone"
                    label="Nomor Telepon"
                    prepend-inner-icon="ri-phone-line"
                    disabled
                  />
                </VCol>
                <VCol cols="12">
                  <VTextarea
                    v-model="tenant.address"
                    label="Alamat"
                    rows="2"
                    prepend-inner-icon="ri-map-pin-line"
                    disabled
                  />
                </VCol>
              </VRow>

              <VAlert type="info" variant="tonal" density="compact" class="mt-2">
                Data informasi toko dikelola oleh super admin.
                Hubungi support jika perlu perubahan.
              </VAlert>
            </VCardText>

            <VDivider />

            <VCardTitle class="pa-4 pb-2">Preferensi Sistem</VCardTitle>
            <VCardText>
              <VRow>
                <VCol cols="12" md="6">
                  <VSelect
                    v-model="settings.timezone"
                    :items="timezoneOptions"
                    label="Zona Waktu"
                    prepend-inner-icon="ri-time-zone-line"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="settings.currency"
                    label="Mata Uang"
                    prepend-inner-icon="ri-money-dollar-circle-line"
                    disabled
                    hint="Saat ini hanya mendukung IDR"
                    persistent-hint
                  />
                </VCol>
              </VRow>
            </VCardText>
          </VCard>

          <!-- ============================================================
               Tab: Pajak & Keuangan
          ============================================================ -->
          <VCard v-show="activeTab === 'tax'">
            <VCardTitle class="pa-4 pb-2">Pajak & Keuangan</VCardTitle>
            <VCardText>
              <VRow>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="taxRatePercent"
                    label="Tarif Pajak (%)"
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    prepend-inner-icon="ri-percent-line"
                    :error-messages="errors.tax_rate"
                    hint="Contoh: 11 untuk PPN 11%"
                    persistent-hint
                  />
                </VCol>
                <VCol cols="12">
                  <VAlert type="info" variant="tonal" density="compact">
                    Tarif pajak saat ini:
                    <strong>{{ formatPercent(settings.tax_rate) }}</strong>
                    — akan dikenakan pada setiap transaksi POS.
                  </VAlert>
                </VCol>
              </VRow>
            </VCardText>
          </VCard>

          <!-- ============================================================
               Tab: Struk
          ============================================================ -->
          <VCard v-show="activeTab === 'receipt'">
            <VCardTitle class="pa-4 pb-2">Pengaturan Struk</VCardTitle>
            <VCardText>
              <VRow>
                <VCol cols="12">
                  <VSwitch
                    v-model="settings.receipt_show_logo"
                    label="Tampilkan Logo di Struk"
                    color="primary"
                    hide-details
                    class="mb-4"
                  />
                </VCol>
                <VCol cols="12">
                  <p class="text-body-2 font-weight-medium mb-2">
                    Logo Khusus Struk (opsional)
                  </p>
                  <div class="d-flex align-center gap-4 flex-wrap">
                    <VAvatar
                      v-if="tenant.receipt_logo"
                      size="64"
                      rounded
                    >
                      <VImg :src="tenant.receipt_logo" />
                    </VAvatar>
                    <VAvatar
                      v-else
                      size="64"
                      rounded
                      color="secondary"
                      variant="tonal"
                    >
                      <VIcon icon="ri-receipt-line" size="26" />
                    </VAvatar>
                    <VFileInput
                      v-model="receiptLogoFile"
                      label="Pilih logo struk baru"
                      accept="image/*"
                      density="compact"
                      prepend-icon=""
                      prepend-inner-icon="ri-image-add-line"
                      hide-details
                      show-size
                      class="flex-grow-1"
                      style="min-width: 220px;"
                    />
                    <VBtn
                      color="primary"
                      variant="tonal"
                      :disabled="!receiptLogoFile"
                      :loading="receiptLogoUploading"
                      @click="uploadReceiptLogo"
                    >
                      Upload
                    </VBtn>
                  </div>
                  <p class="text-caption text-medium-emphasis mt-2 mb-0">
                    Kalau tidak diisi, struk memakai Logo Toko biasa. Cocok untuk versi
                    hitam-putih yang lebih jelas dicetak di printer thermal.
                  </p>
                  <VAlert
                    v-if="receiptLogoError"
                    type="error"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                  >
                    {{ receiptLogoError }}
                  </VAlert>
                </VCol>
                <VCol cols="12">
                  <VTextarea
                    v-model="settings.receipt_footer"
                    label="Pesan Footer Struk"
                    rows="3"
                    placeholder="Terima kasih sudah berkunjung!"
                    prepend-inner-icon="ri-text-snippet"
                    :error-messages="errors.receipt_footer"
                    hint="Tampil di bagian bawah struk thermal"
                    persistent-hint
                    counter="200"
                    maxlength="200"
                  />
                </VCol>
              </VRow>

              <!-- Preview mini -->
              <VCard variant="outlined" class="mt-4 pa-3" max-width="280">
                <div class="text-center text-body-2">
                  <p class="font-weight-bold mb-1">{{ tenant.name || 'Nama Toko' }}</p>
                  <VDivider class="my-2" />
                  <p class="text-caption text-medium-emphasis mb-0">
                    Item 1 ............. Rp 15.000
                  </p>
                  <p class="text-caption text-medium-emphasis mb-0">
                    Item 2 ............. Rp 20.000
                  </p>
                  <VDivider class="my-2" />
                  <p class="text-caption font-weight-bold mb-2">Total Rp 35.000</p>
                  <VDivider class="my-2" />
                  <p class="text-caption">{{ settings.receipt_footer }}</p>
                </div>
              </VCard>
            </VCardText>
          </VCard>

          <!-- ============================================================
               Tab: Email / SMTP
          ============================================================ -->
          <VCard v-show="activeTab === 'email'">
            <VCardTitle class="pa-4 pb-2">Pengaturan Email (SMTP)</VCardTitle>
            <VCardText>
              <VAlert type="info" variant="tonal" density="compact" class="mb-4">
                Dipakai untuk mengirim struk transaksi via email dari halaman POS.
                Kalau pakai Gmail, isi <strong>App Password</strong> (bukan password akun biasa) —
                buat di
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener">
                  myaccount.google.com/apppasswords
                </a>.
              </VAlert>

              <VRow>
                <VCol cols="12" md="8">
                  <VTextField
                    v-model="settings.smtp_host"
                    label="SMTP Host"
                    placeholder="smtp.gmail.com"
                    prepend-inner-icon="ri-server-line"
                    :error-messages="errors.smtp_host"
                  />
                </VCol>
                <VCol cols="12" md="4">
                  <VTextField
                    v-model.number="settings.smtp_port"
                    label="Port"
                    type="number"
                    placeholder="587"
                    :error-messages="errors.smtp_port"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VSelect
                    v-model="settings.smtp_encryption"
                    :items="encryptionOptions"
                    label="Enkripsi"
                    prepend-inner-icon="ri-lock-line"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="settings.smtp_username"
                    label="SMTP Username"
                    placeholder="tokoanda@gmail.com"
                    prepend-inner-icon="ri-user-line"
                    :error-messages="errors.smtp_username"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="settings.smtp_password"
                    label="SMTP Password / App Password"
                    type="password"
                    :placeholder="smtpPasswordConfigured ? '•••••••• (sudah diisi — kosongkan jika tidak ingin ubah)' : 'Masukkan App Password'"
                    prepend-inner-icon="ri-key-line"
                    :error-messages="errors.smtp_password"
                    autocomplete="new-password"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="settings.smtp_from_name"
                    label="Nama Pengirim"
                    placeholder="Nama toko Anda"
                    prepend-inner-icon="ri-user-star-line"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="settings.smtp_from_email"
                    label="Alamat Email Pengirim"
                    placeholder="tokoanda@gmail.com"
                    prepend-inner-icon="ri-mail-send-line"
                    :error-messages="errors.smtp_from_email"
                  />
                </VCol>
              </VRow>

              <VAlert
                :type="smtpPasswordConfigured ? 'success' : 'warning'"
                variant="tonal"
                density="compact"
                class="mt-2"
              >
                <template v-if="smtpPasswordConfigured">
                  SMTP sudah dikonfigurasi. Tombol "Kirim Email" di halaman struk POS siap dipakai.
                </template>
                <template v-else>
                  SMTP belum dikonfigurasi — tombol "Kirim Email" di halaman struk POS belum bisa dipakai sampai ini diisi & disimpan.
                </template>
              </VAlert>
            </VCardText>
          </VCard>

          <!-- ============================================================
               Tab: Payment Gateway
          ============================================================ -->
          <VCard v-show="activeTab === 'payment'">
            <VCardTitle class="pa-4 pb-2">Payment Gateway (QRIS)</VCardTitle>
            <VCardText>
              <VAlert type="info" variant="tonal" density="compact" class="mb-4">
                Dipakai untuk generate QRIS dinamis dari halaman POS. Pilih provider
                di bawah, lalu isi kredensialnya — kredensial provider lain (kalau
                pernah diisi sebelumnya) tetap tersimpan walau tidak sedang ditampilkan,
                jadi tidak perlu isi ulang kalau nanti mau pindah provider.
              </VAlert>

              <VRow>
                <VCol cols="12" md="6">
                  <VSelect
                    v-model="settings.payment_gateway_provider"
                    :items="paymentProviderOptions"
                    label="Provider Aktif"
                    prepend-inner-icon="ri-bank-card-line"
                    :error-messages="errors.payment_gateway_provider"
                  />
                </VCol>
                <VCol cols="12" md="6" class="d-flex align-center">
                  <VSwitch
                    v-model="settings.payment_gateway_is_production"
                    :label="settings.payment_gateway_is_production ? 'Mode Production (transaksi nyata)' : 'Mode Sandbox (testing, belum transaksi nyata)'"
                    color="primary"
                    :error-messages="errors.payment_gateway_is_production"
                  />
                </VCol>
              </VRow>

              <VDivider class="my-4" />

              <!-- Midtrans — hanya tampil kalau provider aktif = midtrans, supaya
                   tenant tidak dibingungkan isian 3 provider sekaligus -->
              <template v-if="settings.payment_gateway_provider === 'midtrans'">
                <p class="text-body-1 font-weight-medium mb-2">
                  <VIcon icon="ri-bank-card-2-line" size="18" class="me-1" />
                  Midtrans
                </p>
                <VRow>
                  <VCol cols="12" md="8">
                    <VTextField
                      v-model="settings.payment_gateway_midtrans_server_key"
                      label="Midtrans Server Key"
                      type="password"
                      :placeholder="midtransServerKeyConfigured ? '•••••••• (sudah diisi — kosongkan jika tidak ingin ubah)' : 'SB-Mid-server-xxxxxxxxxxxxxxxx'"
                      prepend-inner-icon="ri-key-line"
                      :error-messages="errors.payment_gateway_midtrans_server_key"
                      autocomplete="new-password"
                    />
                  </VCol>
                  <VCol cols="12" md="4">
                    <VTextField
                      :model-value="midtransWebhookUrl"
                      label="Webhook / Notification URL"
                      readonly
                      prepend-inner-icon="ri-links-line"
                      hint="Daftarkan URL ini di Midtrans Dashboard > Settings > Configuration"
                      persistent-hint
                    />
                  </VCol>
                </VRow>

                <VAlert
                  :type="midtransServerKeyConfigured ? 'success' : 'warning'"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  <template v-if="midtransServerKeyConfigured">
                    Midtrans server key sudah dikonfigurasi.
                  </template>
                  <template v-else>
                    Midtrans server key belum diisi — QRIS via Midtrans belum bisa dipakai sampai ini diisi & disimpan.
                  </template>
                </VAlert>
              </template>

              <!-- Xendit — hanya tampil kalau provider aktif = xendit -->
              <template v-if="settings.payment_gateway_provider === 'xendit'">
                <p class="text-body-1 font-weight-medium mb-2">
                  <VIcon icon="ri-bank-card-2-line" size="18" class="me-1" />
                  Xendit
                </p>
                <VRow>
                  <VCol cols="12" md="6">
                    <VTextField
                      v-model="settings.payment_gateway_xendit_secret_key"
                      label="Xendit Secret Key"
                      type="password"
                      :placeholder="xenditSecretKeyConfigured ? '•••••••• (sudah diisi — kosongkan jika tidak ingin ubah)' : 'xnd_development_xxxxxxxxxxxxxxxx'"
                      prepend-inner-icon="ri-key-line"
                      :error-messages="errors.payment_gateway_xendit_secret_key"
                      autocomplete="new-password"
                    />
                  </VCol>
                  <VCol cols="12" md="6">
                    <VTextField
                      v-model="settings.payment_gateway_xendit_callback_token"
                      label="Xendit Callback Verification Token"
                      type="password"
                      :placeholder="xenditCallbackTokenConfigured ? '•••••••• (sudah diisi — kosongkan jika tidak ingin ubah)' : 'Dari Xendit Dashboard > Settings > Callbacks'"
                      prepend-inner-icon="ri-shield-keyhole-line"
                      :error-messages="errors.payment_gateway_xendit_callback_token"
                      autocomplete="new-password"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      :model-value="xenditWebhookUrl"
                      label="Webhook URL"
                      readonly
                      prepend-inner-icon="ri-links-line"
                      hint="Daftarkan URL ini di Xendit Dashboard > Settings > Callbacks, lalu salin Verification Token ke field di atas"
                      persistent-hint
                    />
                  </VCol>
                </VRow>

                <VAlert
                  :type="(xenditSecretKeyConfigured && xenditCallbackTokenConfigured) ? 'success' : 'warning'"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  <template v-if="xenditSecretKeyConfigured && xenditCallbackTokenConfigured">
                    Xendit sudah dikonfigurasi lengkap (secret key + callback token).
                  </template>
                  <template v-else>
                    Xendit belum lengkap — isi Secret Key <strong>dan</strong> Callback Verification Token
                    supaya QRIS via Xendit dan konfirmasi pembayarannya bisa berjalan.
                  </template>
                </VAlert>
              </template>

              <!-- iPaymu (Addendum 06, Modul N) — hanya tampil kalau provider aktif = ipaymu -->
              <template v-if="settings.payment_gateway_provider === 'ipaymu'">
                <p class="text-body-1 font-weight-medium mb-2">
                  <VIcon icon="ri-bank-card-2-line" size="18" class="me-1" />
                  iPaymu
                </p>
                <VRow>
                  <VCol cols="12" md="6">
                    <VTextField
                      v-model="settings.payment_gateway_ipaymu_va"
                      label="iPaymu VA (Virtual Account/Merchant Number)"
                      type="password"
                      :placeholder="ipaymuVaConfigured ? '•••••••• (sudah diisi — kosongkan jika tidak ingin ubah)' : 'Dari iPaymu Dashboard > Integrasi'"
                      prepend-inner-icon="ri-shield-keyhole-line"
                      :error-messages="errors.payment_gateway_ipaymu_va"
                      autocomplete="new-password"
                    />
                  </VCol>
                  <VCol cols="12" md="6">
                    <VTextField
                      v-model="settings.payment_gateway_ipaymu_api_key"
                      label="iPaymu API Key"
                      type="password"
                      :placeholder="ipaymuApiKeyConfigured ? '•••••••• (sudah diisi — kosongkan jika tidak ingin ubah)' : 'Dari iPaymu Dashboard > Integrasi'"
                      prepend-inner-icon="ri-key-line"
                      :error-messages="errors.payment_gateway_ipaymu_api_key"
                      autocomplete="new-password"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      :model-value="ipaymuWebhookUrl"
                      label="Webhook / Notify URL"
                      readonly
                      prepend-inner-icon="ri-links-line"
                      hint="Daftarkan URL ini sebagai Notify URL di iPaymu Dashboard > Integrasi"
                      persistent-hint
                    />
                  </VCol>
                </VRow>

                <VAlert
                  :type="(ipaymuVaConfigured && ipaymuApiKeyConfigured) ? 'success' : 'warning'"
                  variant="tonal"
                  density="compact"
                  class="mt-2"
                >
                  <template v-if="ipaymuVaConfigured && ipaymuApiKeyConfigured">
                    iPaymu sudah dikonfigurasi lengkap (VA + API Key).
                  </template>
                  <template v-else>
                    iPaymu belum lengkap — isi VA <strong>dan</strong> API Key
                    supaya QRIS via iPaymu dan konfirmasi pembayarannya bisa berjalan.
                  </template>
                </VAlert>
              </template>
            </VCardText>
          </VCard>

          <!-- ============================================================
               Tab: Inventory
          ============================================================ -->
          <VCard v-show="activeTab === 'inventory'">
            <VCardTitle class="pa-4 pb-2">Pengaturan Inventory</VCardTitle>
            <VCardText>
              <VRow>
                <VCol cols="12">
                  <VSwitch
                    v-model="settings.allow_negative_stock"
                    label="Izinkan Stok Negatif"
                    color="warning"
                    hide-details
                  />
                  <p class="text-caption text-medium-emphasis mt-1 ms-10">
                    Jika aktif, transaksi tetap bisa diproses walau stok bahan baku habis.
                    <strong class="text-warning">Tidak direkomendasikan.</strong>
                  </p>
                </VCol>
                <VCol cols="12" md="6" class="mt-2">
                  <VTextField
                    v-model.number="settings.low_stock_threshold"
                    label="Batas Stok Kritis"
                    type="number"
                    min="0"
                    prepend-inner-icon="ri-alert-line"
                    :error-messages="errors.low_stock_threshold"
                    hint="Notifikasi muncul jika stok ≤ nilai ini"
                    persistent-hint
                    suffix="unit"
                  />
                </VCol>
              </VRow>
            </VCardText>
          </VCard>

          <!-- ============================================================
               Tab: Absensi — Addendum 08 patch. Pengaturan lokasi toko
               (geofence) untuk kiosk absen PIN/QR (kiosk.vue, Modul AA).
               Attendance.manage-gated, sama gerbang dengan endpoint PUT-nya.
          ============================================================ -->
          <VCard v-if="canManageAttendance" v-show="activeTab === 'attendance'">
            <VCardTitle class="pa-4 pb-2">
              <VIcon icon="ri-fingerprint-line" class="me-2" />
              Pengaturan Absensi
            </VCardTitle>
            <VCardText>
              <VAlert type="info" variant="tonal" density="compact" class="mb-4">
                Mengatur fitur absensi (PIN/QR) karyawan dan lokasi toko untuk
                validasi geofence saat clock-in/out di kiosk absen. Kalau
                "Aktifkan Fitur Absensi" nonaktif, fitur ini tidak berpengaruh
                sama sekali ke alur POS/shift kasir yang sudah ada.
              </VAlert>

              <VAlert
                v-if="attendanceSuccessMessage"
                type="success"
                variant="tonal"
                density="compact"
                class="mb-4"
                closable
                @click:close="attendanceSuccessMessage = ''"
              >
                {{ attendanceSuccessMessage }}
              </VAlert>

              <VRow>
                <VCol cols="12" md="6">
                  <VSwitch
                    v-model="attendanceSettings.is_active"
                    label="Aktifkan Fitur Absensi"
                    color="primary"
                    hide-details
                  />
                  <p class="text-caption text-medium-emphasis mt-1 ms-10">
                    Mengaktifkan kiosk absen PIN/QR untuk semua staff toko.
                  </p>
                </VCol>
                <VCol cols="12" md="6">
                  <VSwitch
                    v-model="attendanceSettings.require_attendance_for_shift"
                    label="Wajib Absen Sebelum Buka Shift Kasir"
                    color="primary"
                    hide-details
                    :disabled="!attendanceSettings.is_active"
                  />
                  <p class="text-caption text-medium-emphasis mt-1 ms-10">
                    Kalau aktif, kasir wajib clock-in dulu sebelum bisa membuka
                    shift. Terpisah dari toggle di atas — bisa aktifkan
                    absensi tanpa mewajibkannya untuk buka shift.
                  </p>
                </VCol>
                <VCol cols="12" md="6">
                  <VSwitch
                    v-model="attendanceSettings.photo_capture_enabled"
                    label="Aktifkan Foto Verifikasi Saat Absen"
                    color="primary"
                    hide-details
                    :disabled="!attendanceSettings.is_active"
                  />
                  <p class="text-caption text-medium-emphasis mt-1 ms-10">
                    Kalau aktif, staff wajib ambil 1 foto dari kamera kiosk
                    sebelum absen tercatat. Foto otomatis terkompresi kecil
                    (~50-80KB) dan disimpan maksimal 40 hari — Owner dapat
                    notifikasi 10 hari sebelum foto lama dihapus otomatis
                    (data jam masuk/keluar TIDAK ikut terhapus).
                  </p>
                </VCol>
              </VRow>

              <VDivider class="my-4" />

              <p class="text-body-1 font-weight-medium mb-1">
                Lokasi Toko & Radius Geofence
              </p>
              <p class="text-caption text-medium-emphasis mb-4">
                Staff harus berada dalam radius ini dari lokasi toko untuk
                bisa clock-in/out. Kalau lokasi belum diisi, validasi geofence
                dilewati (staff bisa absen dari mana saja).
              </p>

              <VRow>
                <VCol cols="12" md="4">
                  <VTextField
                    v-model="attendanceSettings.store_latitude"
                    label="Latitude"
                    type="number"
                    step="0.0000001"
                    placeholder="-6.2000000"
                    prepend-inner-icon="ri-map-pin-line"
                    :error-messages="attendanceErrors.store_latitude"
                  />
                </VCol>
                <VCol cols="12" md="4">
                  <VTextField
                    v-model="attendanceSettings.store_longitude"
                    label="Longitude"
                    type="number"
                    step="0.0000001"
                    placeholder="106.8166600"
                    prepend-inner-icon="ri-map-pin-line"
                    :error-messages="attendanceErrors.store_longitude"
                  />
                </VCol>
                <VCol cols="12" md="4" class="d-flex align-center">
                  <VBtn
                    variant="tonal"
                    prepend-icon="ri-crosshair-2-line"
                    :loading="gpsLoading"
                    @click="useCurrentLocation"
                  >
                    Ambil Lokasi Saat Ini
                  </VBtn>
                </VCol>
              </VRow>

              <VAlert v-if="gpsError" type="warning" variant="tonal" density="compact" class="mb-2">
                {{ gpsError }}
              </VAlert>

              <VRow>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="attendanceSettings.geofence_radius_meters"
                    label="Radius Geofence (meter)"
                    type="number"
                    min="1"
                    prepend-inner-icon="ri-radar-line"
                    :error-messages="attendanceErrors.geofence_radius_meters"
                  />
                </VCol>
                <VCol cols="12" md="6">
                  <VTextField
                    v-model="attendanceSettings.late_tolerance_minutes"
                    label="Toleransi Keterlambatan (menit)"
                    type="number"
                    min="0"
                    prepend-inner-icon="ri-time-line"
                    :error-messages="attendanceErrors.late_tolerance_minutes"
                    hint="Staff dianggap terlambat kalau clock-in melewati jadwal + toleransi ini."
                    persistent-hint
                  />
                </VCol>
              </VRow>

              <VAlert
                v-if="!attendanceSettings.store_latitude || !attendanceSettings.store_longitude"
                type="warning"
                variant="tonal"
                density="compact"
                class="mt-4"
              >
                Lokasi toko belum diatur — validasi geofence akan DILEWATI (staff
                bisa clock-in/out dari lokasi mana saja) sampai lokasi diisi & disimpan.
              </VAlert>
            </VCardText>
            <VCardActions class="justify-end pa-4">
              <VBtn
                color="primary"
                size="large"
                prepend-icon="ri-save-line"
                :loading="attendanceLoading"
                @click="saveAttendanceSettings"
              >
                Simpan Pengaturan Absensi
              </VBtn>
            </VCardActions>
          </VCard>

          <!-- ============================================================
               Tab: Tipe Bisnis — Addendum 10, Modul AL. Toggle F&B/Retail,
               Tenant.manage_features-gated, sama gerbang dengan endpoint
               PUT-nya sendiri.
          ============================================================ -->
          <VCard v-if="canManageFeatures" v-show="activeTab === 'business-type'">
            <VCardTitle class="pa-4 pb-2">
              <VIcon icon="ri-store-3-line" class="me-2" />
              Tipe Bisnis
            </VCardTitle>
            <VCardText>
              <VAlert type="info" variant="tonal" density="compact" class="mb-4">
                Mengatur fitur mana yang aktif untuk toko Anda. Boleh aktifkan
                keduanya sekaligus (hybrid F&B + Retail) — ini BUKAN mode
                switch yang menghapus fitur yang sudah ada, hanya mengatur
                visibilitas menu.
              </VAlert>

              <VAlert
                v-if="businessTypeSuccessMessage"
                type="success"
                variant="tonal"
                density="compact"
                class="mb-4"
                closable
                @click:close="businessTypeSuccessMessage = ''"
              >
                {{ businessTypeSuccessMessage }}
              </VAlert>

              <VRow>
                <VCol cols="12" md="6">
                  <VSwitch
                    v-model="businessType.is_fnb_enabled"
                    label="Aktifkan Fitur F&B"
                    color="primary"
                    hide-details
                  />
                  <p class="text-caption text-medium-emphasis mt-1 ms-10">
                    Self-Order, KDS, Denah Meja, Modifier, dan Resep/HPP.
                  </p>
                </VCol>
                <VCol cols="12" md="6">
                  <VSwitch
                    v-model="businessType.is_retail_enabled"
                    label="Aktifkan Fitur Retail"
                    color="primary"
                    hide-details
                  />
                  <p class="text-caption text-medium-emphasis mt-1 ms-10">
                    Scan barcode, harga grosir bertingkat, tracking kedaluwarsa.
                  </p>
                </VCol>
              </VRow>

              <VAlert
                v-if="!businessType.is_fnb_enabled && !businessType.is_retail_enabled"
                type="warning"
                variant="tonal"
                density="compact"
                class="mt-4"
              >
                Kedua fitur nonaktif — pastikan ini disengaja, POS Kasir dan
                menu umum lainnya tetap berjalan normal.
              </VAlert>
            </VCardText>
            <VCardActions class="justify-end pa-4">
              <VBtn
                color="primary"
                size="large"
                prepend-icon="ri-save-line"
                :loading="businessTypeLoading"
                @click="saveBusinessType"
              >
                Simpan Tipe Bisnis
              </VBtn>
            </VCardActions>
          </VCard>

          <!-- ============================================================
               Tab: AI Status — BARU
          ============================================================ -->
          <VCard v-show="activeTab === 'ai-status'">
            <VCardTitle class="pa-4 pb-2">
              <VIcon icon="ri-robot-line" class="me-2" />
              Status Layanan AI
            </VCardTitle>
            <VCardText>
              <p class="text-body-2 text-medium-emphasis mb-6">
                Pemantauan kesehatan layanan Voice-to-Order & Smart Receipt Scanner.
                Kedua fitur ini SELALU punya jalur manual yang berfungsi normal,
                apa pun status di halaman ini.
              </p>

              <VRow>
                <VCol cols="12" md="6">
                  <VCard>
                    <VCardText class="d-flex align-center">
                      <VIcon icon="ri-mic-line" size="32" class="me-4 text-medium-emphasis" />
                      <div class="flex-grow-1">
                        <div class="text-subtitle-1 font-weight-medium">
                          Voice-to-Order
                        </div>
                        <div class="text-caption text-medium-emphasis">
                          Terakhir dicek: {{ formatCheckedAt(voiceStatus?.checked_at) }}
                        </div>
                      </div>
                      <VChip
                        :color="statusMeta(voiceStatus?.status).color"
                        :prepend-icon="statusMeta(voiceStatus?.status).icon"
                        variant="tonal"
                      >
                        {{ statusMeta(voiceStatus?.status).label }}
                      </VChip>
                    </VCardText>
                    <VCardText v-if="voiceStatus?.status !== 'healthy'" class="pt-0">
                      <VAlert type="warning" variant="tonal" density="compact">
                        {{ voiceStatus?.message }}
                      </VAlert>
                    </VCardText>
                  </VCard>
                </VCol>

                <VCol cols="12" md="6">
                  <VCard>
                    <VCardText class="d-flex align-center">
                      <VIcon icon="ri-camera-line" size="32" class="me-4 text-medium-emphasis" />
                      <div class="flex-grow-1">
                        <div class="text-subtitle-1 font-weight-medium">
                          Smart Receipt Scanner
                        </div>
                        <div class="text-caption text-medium-emphasis">
                          Terakhir dicek: {{ formatCheckedAt(receiptStatus?.checked_at) }}
                        </div>
                      </div>
                      <VChip
                        :color="statusMeta(receiptStatus?.status).color"
                        :prepend-icon="statusMeta(receiptStatus?.status).icon"
                        variant="tonal"
                      >
                        {{ statusMeta(receiptStatus?.status).label }}
                      </VChip>
                    </VCardText>
                    <VCardText v-if="receiptStatus?.status !== 'healthy'" class="pt-0">
                      <VAlert type="warning" variant="tonal" density="compact">
                        {{ receiptStatus?.message }}
                      </VAlert>
                    </VCardText>
                  </VCard>
                </VCol>
              </VRow>
            </VCardText>
            <VCardActions class="justify-end">
              <VBtn
                variant="text"
                prepend-icon="ri-refresh-line"
                :loading="aiLoading"
                @click="loadAiStatus"
              >
                Cek Ulang Sekarang
              </VBtn>
            </VCardActions>
          </VCard>

          <!-- ============================================================
               Tab: Langganan — Addendum 05, Modul L (perpanjangan
               self-service). Hanya render kalau owner (tab-nya sendiri
               sudah disaring dari `tabs`, ini jaga-jaga tambahan).
          ============================================================ -->
          <VCard v-if="canRenewSubscription" v-show="activeTab === 'langganan'">
            <VCardTitle class="pa-4 pb-2">
              <VIcon icon="ri-vip-crown-line" class="me-2" />
              Langganan
            </VCardTitle>
            <VCardText>
              <VProgressLinear v-if="subscriptionLoading" indeterminate color="primary" class="mb-4" />

              <VAlert
                v-if="subscriptionInfo"
                :type="subscriptionInfo.is_expired ? 'error' : 'success'"
                variant="tonal"
                class="mb-6"
                :icon="subscriptionInfo.is_expired ? 'ri-lock-line' : 'ri-shield-check-line'"
              >
                <strong>Paket saat ini: {{ subscriptionInfo.plan?.name ?? '-' }}</strong><br>
                <template v-if="subscriptionInfo.is_expired">
                  Langganan sudah berakhir pada {{ formatDate(subscriptionInfo.expires_at) }}. Transaksi baru
                  terkunci sampai Anda perpanjang.
                </template>
                <template v-else>
                  Aktif sampai {{ formatDate(subscriptionInfo.expires_at) }}.
                </template>
              </VAlert>
              <VAlert v-else-if="!subscriptionLoading" type="info" variant="tonal" class="mb-6">
                Toko Anda belum punya data langganan. Hubungi support PintuKasir.
              </VAlert>

              <!-- Addendum 13, Modul BG: unduh ulang invoice PDF pendaftaran awal (sama file yang dilampirkan di email "Pendaftaran Diterima"). Disembunyikan kalau tenant dibuat manual lewat /platform, tidak pernah lewat alur /daftar. -->
              <VBtn
                v-if="invoiceAvailable"
                variant="tonal"
                color="secondary"
                prepend-icon="ri-file-download-line"
                class="mb-6"
                :loading="invoiceDownloading"
                @click="downloadInvoice"
              >
                Unduh Invoice Pendaftaran (PDF)
              </VBtn>

              <VDivider class="mb-6" />

              <p class="text-body-1 font-weight-medium mb-4">Perpanjang / Ganti Paket</p>

              <VRow>
                <VCol cols="12" md="4">
                  <VBtnToggle
                    v-model="billingPeriod"
                    mandatory
                    color="primary"
                    divided
                    density="comfortable"
                    class="mb-4 billing-toggle"
                  >
                    <VBtn
                      v-for="period in BILLING_PERIODS"
                      :key="period"
                      :value="period"
                    >
                      {{ BILLING_PERIOD_LABELS[period] }}
                    </VBtn>
                  </VBtnToggle>
                </VCol>
              </VRow>

              <VRow>
                <VCol
                  v-for="plan in availablePlans"
                  :key="plan.id"
                  cols="12"
                  md="4"
                >
                  <VCard
                    :variant="selectedPlanId === plan.id ? 'tonal' : 'outlined'"
                    :color="selectedPlanId === plan.id ? 'primary' : undefined"
                    style="cursor: pointer;"
                    @click="selectedPlanId = plan.id"
                  >
                    <VCardText>
                      <div class="d-flex align-center justify-space-between mb-1">
                        <span class="text-h6">{{ plan.name }}</span>
                        <VIcon v-if="selectedPlanId === plan.id" icon="ri-checkbox-circle-fill" color="primary" />
                      </div>
                      <p class="text-caption text-medium-emphasis mb-2">{{ plan.tagline }}</p>
                      <p class="text-body-1 font-weight-medium mb-0">
                        {{ formatRupiah(perMonthPrice(plan, billingPeriod)) }}
                        <span class="text-caption text-medium-emphasis">/ bulan</span>
                      </p>
                    </VCardText>
                  </VCard>
                </VCol>
              </VRow>

              <VAlert v-if="renewError" type="error" variant="tonal" density="compact" class="mt-4">
                {{ renewError }}
              </VAlert>

              <p class="text-caption text-medium-emphasis mt-4 mb-0">
                Anda akan diarahkan ke halaman pembayaran Xendit. Langganan diperpanjang otomatis begitu
                pembayaran diterima — sisa waktu langganan yang belum terpakai (jika masih aktif) tidak hilang.
              </p>
            </VCardText>
            <VCardActions class="justify-end pa-4">
              <VBtn
                color="primary"
                size="large"
                :disabled="!selectedPlanId"
                :loading="renewLoading"
                prepend-icon="ri-bank-card-line"
                @click="renewSubscription"
              >
                Perpanjang & Bayar
              </VBtn>
            </VCardActions>
          </VCard>

          <!-- ============================================================
               Tab: Addon — Addendum 12, Modul AZ (pembelian/perpanjangan
               addon berbayar terpisah, mis. Absensi Karyawan). Hanya
               render kalau owner (tab-nya sendiri sudah disaring dari
               `tabs`, ini jaga-jaga tambahan) — sama pola dengan tab
               Langganan di atas.
          ============================================================ -->
          <VCard v-if="canManageAddons" v-show="activeTab === 'addon'">
            <VCardTitle class="pa-4 pb-2">
              <VIcon icon="ri-puzzle-line" class="me-2" />
              Kelola Addon
            </VCardTitle>
            <VCardText>
              <VProgressLinear v-if="addonsLoading" indeterminate color="primary" class="mb-4" />

              <VAlert v-if="addonPurchaseError" type="error" variant="tonal" density="compact" class="mb-4">
                {{ addonPurchaseError }}
              </VAlert>

              <VAlert v-if="!addonsLoading && !addons.length" type="info" variant="tonal" class="mb-4">
                Belum ada addon yang tersedia.
              </VAlert>

              <VRow>
                <VCol
                  v-for="addon in addons"
                  :key="addon.id"
                  cols="12"
                  md="6"
                >
                  <VCard variant="outlined">
                    <VCardText>
                      <div class="d-flex align-center justify-space-between mb-1">
                        <span class="text-h6">{{ addon.name }}</span>
                        <VChip
                          v-if="addon.is_active_for_tenant"
                          color="success"
                          size="small"
                          variant="tonal"
                        >
                          Aktif sampai {{ formatDate(addon.expires_at) }}
                        </VChip>
                        <VChip v-else size="small" variant="tonal">
                          Belum Aktif
                        </VChip>
                      </div>
                      <p class="text-caption text-medium-emphasis mb-4">{{ addon.description }}</p>

                      <VBtnToggle
                        v-model="addonBillingPeriod[addon.id]"
                        mandatory
                        color="primary"
                        divided
                        density="comfortable"
                        class="mb-4 billing-toggle"
                      >
                        <VBtn value="monthly">Bulanan</VBtn>
                        <VBtn value="yearly">Tahunan</VBtn>
                      </VBtnToggle>

                      <p class="text-body-1 font-weight-medium mb-4">
                        {{ formatRupiah(addonBillingPeriod[addon.id] === 'yearly' ? addon.price_yearly_per_month : addon.price_monthly) }}
                        <span class="text-caption text-medium-emphasis">
                          / bulan{{ addonBillingPeriod[addon.id] === 'yearly' ? ' (ditagih tahunan)' : '' }}
                        </span>
                      </p>

                      <VBtn
                        color="primary"
                        block
                        :loading="addonPurchaseLoading[addon.id]"
                        prepend-icon="ri-bank-card-line"
                        @click="purchaseAddon(addon)"
                      >
                        {{ addon.is_active_for_tenant ? 'Perpanjang & Bayar' : 'Beli & Aktifkan' }}
                      </VBtn>
                    </VCardText>
                  </VCard>
                </VCol>
              </VRow>

              <p class="text-caption text-medium-emphasis mt-4 mb-0">
                Anda akan diarahkan ke halaman pembayaran Xendit. Addon otomatis aktif begitu pembayaran diterima
                — sisa waktu addon yang belum terpakai (jika masih aktif) tidak hilang.
              </p>
            </VCardText>
          </VCard>

          <!-- Save button — semua tab KECUALI ai-status, langganan, addon &
               attendance (masing-masing punya tombol aksi sendiri, endpoint terpisah) -->
          <div v-if="!['ai-status', 'langganan', 'addon', 'attendance'].includes(activeTab)" class="d-flex justify-end mt-4">
            <VBtn
              type="submit"
              color="primary"
              size="large"
              :loading="loading"
              prepend-icon="ri-save-line"
            >
              Simpan Pengaturan
            </VBtn>
          </div>
        </VForm>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
/*
 * Override template global (@core/.../_button.scss ".v-btn-toggle .v-btn")
 * yang memaksa SEMUA tombol di dalam VBtnToggle jadi kotak !important --
 * dirancang untuk toggle ikon-saja, bukan tombol berlabel teks seperti
 * "3 Bulan"/"6 Bulan"/"Tahunan" di sini. Pola sama seperti pages/daftar/index.vue.
 */
.billing-toggle :deep(.v-btn) {
  inline-size: auto !important;
  padding-inline: 16px !important;
}
</style>
